import { useEffect, useState } from 'react';
import { Sparkles } from 'lucide-react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useSoteriaSidePanelStore } from '@/features/SoteriaAI/stores/sidePanelStore';
import { SOTERIA_AGENT } from '@/features/SoteriaAI/agentKind';

// First-run hint: storage key for the "Try Soteria" introduction popover.
// Once a user has either dismissed the hint or actually opened the panel,
// we stop nagging them.
const HINT_DISMISSED_KEY = 'biasafe_soteria_hint_dismissed_v1';

/**
 * HeaderAIAssistant
 * -----------------
 * The Sparkles button in the top header. Clicking it now opens the
 * Soteria side panel (a right-side drawer) instead of navigating to
 * /soteria — users can ask a question without losing their place in
 * whatever screen they were on.
 *
 * First-run affordance:
 *   - The icon is large (h-6) and uses a soft purple gradient instead
 *     of the muted ghost style — it should READ as "this is the AI."
 *   - A small purple dot pulses next to it until the user has interacted.
 *   - On the very first session, a popover auto-opens with "Try it out
 *     — ask Soteria a question." The state is stored in localStorage so
 *     it doesn't reappear every page load.
 *   - On every subsequent session the popover only appears on hover, so
 *     it stops feeling like a nag.
 */
export const HeaderAIAssistant = () => {
  const isOpen = useSoteriaSidePanelStore((s) => s.isOpen);
  const openFor = useSoteriaSidePanelStore((s) => s.openFor);
  const close = useSoteriaSidePanelStore((s) => s.close);
  const [hintDismissed, setHintDismissed] = useState(true); // optimistic, set from storage below
  const [autoHintOpen, setAutoHintOpen] = useState(false);

  // Read the dismissal flag on mount. We start with `hintDismissed=true`
  // so the auto-popover never flashes for returning users.
  useEffect(() => {
    try {
      const seen = window.localStorage.getItem(HINT_DISMISSED_KEY) === '1';
      setHintDismissed(seen);
      if (!seen) {
        // Wait a beat so the icon has time to mount + animate in before
        // the popover anchors to it.
        const id = window.setTimeout(() => setAutoHintOpen(true), 900);
        return () => window.clearTimeout(id);
      }
    } catch {
      // localStorage blocked (incognito etc.) — just don't show the hint.
    }
  }, []);

  const dismissHint = () => {
    setAutoHintOpen(false);
    setHintDismissed(true);
    try {
      window.localStorage.setItem(HINT_DISMISSED_KEY, '1');
    } catch {
      // ignore quota / disabled
    }
  };

  const handleClick = () => {
    dismissHint();
    // One assistant everywhere: Soteria routes the turn to the right
    // specialist itself, so the drawer no longer depends on the route.
    // Clicking again while open just closes it.
    if (isOpen) {
      close();
    } else {
      openFor(SOTERIA_AGENT);
    }
  };

  return (
    <Popover open={autoHintOpen} onOpenChange={(v) => !v && dismissHint()}>
      <PopoverTrigger asChild>
        {/* Ported from the PMS Figma TopHeader chatbot button: ghost h-9 w-9
            pill, muted Sparkles at rest, and a tinted active state (with the
            ping dot) while the Soteria panel is open. The Figma named a fixed
            violet; it is expressed as the primary token now, so the active
            state stays a tint of the brand colour in either theme rather than
            a light patch on a dark header. */}
        <Button
          variant="ghost"
          size="sm"
          onClick={handleClick}
          aria-label="Soteria AI"
          title="Soteria AI"
          className={cn(
            'relative h-9 w-9 p-0 rounded-md border transition-all duration-200',
            isOpen
              ? 'border-primary/30 bg-primary/10 text-primary'
              : 'border-border/60 bg-card text-foreground hover:bg-muted/50',
          )}
        >
          <Sparkles className="h-5 w-5" strokeWidth={1.75} />
          {isOpen ? (
            <span className="absolute -top-1 -right-1 flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary/60 opacity-75" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-primary" />
            </span>
          ) : (
            !hintDismissed && (
              <span className="absolute top-0 right-0 flex h-2.5 w-2.5">
                <span className="absolute inline-flex h-full w-full rounded-full bg-primary/60 opacity-75 animate-ping" />
                <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-primary" />
              </span>
            )
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        side="bottom"
        align="end"
        sideOffset={10}
        className="w-64 p-3 bg-popover border border-border shadow-lg rounded-xl"
      >
        <div className="flex items-start gap-2">
          <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-gradient-to-br from-primary to-primary/70 shrink-0">
            <Sparkles className="h-4 w-4 text-primary-foreground" />
          </span>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-foreground">Try Soteria AI</p>
            <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
              Ask anything: build a strategy, explain a metric, summarise a
              backtest. Right here, without leaving this page.
            </p>
            <div className="mt-2 flex justify-end">
              <button
                type="button"
                onClick={handleClick}
                className="text-xs font-medium text-primary hover:text-primary/80"
              >
                Open assistant →
              </button>
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};
